import { Controller } from '@hotwired/stimulus';

/**
 * Checkbox Group Controller
 *
 * Manages a group of checkboxes with an optional "select all / deselect all"
 * toggle button and an optional submit button that is disabled while nothing is
 * checked. Disabled checkboxes (e.g. contacts without an email address, or
 * already-added rows) are excluded from bulk operations and from the count, but
 * remain visible in the group.
 *
 * Every target except `checkbox` is OPTIONAL — a group can omit the toggle, the
 * submit, the counter, and the search and still work. The opt-in behaviours
 * (live counter text, live submit LABEL, client-side filter, live row-count
 * badge) only engage when their target/value is present, so existing callers
 * that wire only `checkbox`/`toggleButton`/`submitButton` are unaffected.
 *
 * Targets:
 * - checkbox: Individual checkbox inputs in the group (REQUIRED).
 * - toggleButton: The button that triggers select all / deselect all (optional).
 * - submitButton: The form submit button — disabled when no enabled checkbox is
 *   checked; its label is rewritten ONLY when `submitTemplate` is set (optional).
 * - counter: A live « N sélectionné(s) » element, rewritten from `counterTemplate`
 *   (+ an optional « · M déjà… » suffix from `counterDisabledTemplate`) (optional).
 * - search: A text input filtering `row` targets by their haystack (optional).
 * - row: Selectable rows whose search key is `data-search-haystack`
 *   (server-lowercased), either ON the row itself (annuaire/clause/effectif rows)
 *   or on a DESCENDANT sub-container (the picker's tab-0 rows moved it onto their
 *   re-rendered `…-identity` node so a W3 rename refreshes it — revue xhigh F9b).
 *   The filter reads the row attribute first, then falls back to the descendant.
 *   Their count also feeds the optional `rowCount` badge (optional).
 * - noResults: A hidden-by-default node (e.g. a « Aucun résultat » CTA) revealed
 *   by `filter()` when the search query matches none of the `row` targets
 *   (optional — a group with no `noResults` target and/or no `row` targets is
 *   unaffected, filter() simply skips the toggle).
 * - rowCount: A live count badge (e.g. a tab pill) rewritten with the number of
 *   `row` targets as rows are appended/removed by Turbo streams. Only rows in
 *   THIS controller's scope are counted — rows inside a nested `checkbox-group`
 *   scope belong to that controller, not this one (optional).
 *
 * Values:
 * - selectAllLabel: Toggle label when not all enabled checkboxes are checked
 *   (default: "Tout sélectionner").
 * - deselectAllLabel: Toggle label when all enabled checkboxes are checked
 *   (default: "Tout désélectionner").
 * - counterTemplate (default ''): contains a "{count}" placeholder,
 *   e.g. "{count} intervenant(s) sélectionné(s)". Empty → the counter text is
 *   left untouched.
 * - counterDisabledTemplate (default ''): an OPTIONAL suffix appended to the
 *   counter, with a "{count}" placeholder, e.g. " · {count} déjà au CR". It is
 *   rendered ONLY when the group holds at least one DISABLED checkbox (else a
 *   pristine picker would read « 0 sélectionnée(s) · 0 déjà au CR »). Empty (the
 *   default) → no suffix is ever appended, so callers that omit it get a
 *   byte-identical counter.
 * - submitTemplate (default ''): submit label when count > 0, with a "{count}"
 *   placeholder, e.g. "Ajouter {count} clause(s)". Empty → the submit LABEL is
 *   left untouched (disable-only — the button may contain an icon).
 * - submitZero (default ''): submit label when count === 0, e.g. "Ajouter".
 *
 * Actions:
 * - toggleAll: Selects or deselects all enabled checkboxes and updates state.
 * - updateToggleLabel: Recalculates toggle label + submit state + counter
 *   (call on checkbox change).
 * - filter: Hide/show `row` targets whose haystack doesn't include the query
 *   (call on `input` of the search field).
 * - clearSearch: Empty the search field and re-reveal every row. Bind to whatever
 *   ends the search session (the CR annexes modal binds `modal:close`) — NEVER to
 *   a stream replace, which must preserve the live query (F11).
 *
 * Lifecycle callbacks:
 * - checkboxTarget{Connected,Disconnected}: recount when a checkbox enters/leaves
 *   the DOM. A pre-checked row appended by a Turbo stream emits no `change`, and
 *   a removed row must drop out of both N (checked) and M (disabled) — these keep
 *   the counter/submit honest without a synthetic `change`.
 * - rowTarget{Connected,Disconnected}: refresh the optional `rowCount` badge as
 *   rows are appended/removed, AND re-run the filter when a search is active so a
 *   row appended by a Turbo stream (W1) can't dodge the current query (F11).
 * All four read ONLY framework target accessors — never state initialised in
 * connect() — so they are safe even when Stimulus fires them BEFORE connect().
 *
 * This controller adds NO global (document/window) listeners — every behaviour
 * is wired via `data-action`, so there is nothing to clean up in `disconnect()`.
 *
 * @example
 * <div data-controller="checkbox-group">
 *   <button type="button"
 *           data-checkbox-group-target="toggleButton"
 *           data-action="click->checkbox-group#toggleAll">
 *     Tout sélectionner
 *   </button>
 *   <input type="checkbox"
 *          name="contact_ids[]"
 *          value="1"
 *          data-checkbox-group-target="checkbox"
 *          data-action="change->checkbox-group#updateToggleLabel">
 *   <input type="checkbox"
 *          disabled
 *          data-checkbox-group-target="checkbox">
 *   <button type="submit"
 *           data-checkbox-group-target="submitButton">
 *     Envoyer
 *   </button>
 * </div>
 */
export default class extends Controller {
  static targets = [
    'checkbox',
    'toggleButton',
    'submitButton',
    'counter',
    'search',
    'row',
    'noResults',
    'rowCount',
  ];
  static values = {
    selectAllLabel: { type: String, default: 'Tout sélectionner' },
    deselectAllLabel: { type: String, default: 'Tout désélectionner' },
    counterTemplate: { type: String, default: '' },
    counterDisabledTemplate: { type: String, default: '' },
    submitTemplate: { type: String, default: '' },
    submitZero: { type: String, default: '' },
  };

  connect() {
    this.#updateState();
  }

  // Recount when a checkbox enters/leaves the DOM. Needed because a pre-checked
  // row appended by a Turbo stream (W1/W2) emits no `change` event, and a removed
  // row (W4) must drop out of N (checked) and M (disabled). These callbacks call
  // #updateState() and NOTHING else — no state initialised in connect() is touched,
  // so they stay safe when Stimulus fires them before connect().
  checkboxTargetConnected() {
    this.#updateState();
  }

  checkboxTargetDisconnected() {
    this.#updateState();
  }

  // Keep the optional live row-count badge (a tab pill) in step as rows are
  // appended/removed. No-op when the `rowCount` target is absent. Also re-runs the
  // filter when a search is active (F11), so a row streamed in mid-search obeys the
  // current query instead of appearing unfiltered.
  rowTargetConnected() {
    this.#updateRowCount();
    this.#refilterIfSearching();
  }

  rowTargetDisconnected() {
    this.#updateRowCount();
    this.#refilterIfSearching();
  }

  /**
   * Toggles all enabled checkboxes on or off, then updates state.
   * If all enabled checkboxes are currently checked → unchecks them.
   * Otherwise → checks all enabled checkboxes.
   */
  toggleAll(event) {
    event.preventDefault();
    const enabled = this.#enabledCheckboxes();
    const allChecked = enabled.length > 0 && enabled.every((cb) => cb.checked);
    enabled.forEach((cb) => {
      cb.checked = !allChecked;
    });
    this.#updateState();
  }

  /**
   * Recalculates toggle label, submit button state/label, and the counter.
   * Intended to be called whenever an individual checkbox changes.
   */
  updateToggleLabel() {
    this.#updateState();
  }

  /**
   * Client-side text filter. Bind to `input` on the search field. Matches the
   * lowercased query against each row's server-lowercased `data-search-haystack`.
   * No-op when there is no search target.
   */
  filter() {
    if (!this.hasSearchTarget) return;

    const query = this.searchTarget.value.trim().toLowerCase();

    this.rowTargets.forEach((row) => {
      // F9b — read the row's own `data-search-haystack` first, then fall back to a
      // descendant carrying it (the picker's tab-0 rows moved it onto their
      // re-rendered `…-identity` node). `??` (not `||`) preserves a direct
      // empty-string haystack byte-identically for the other callers — only a
      // MISSING row attribute falls through to the descendant.
      const haystack =
        row.dataset.searchHaystack ??
        row.querySelector('[data-search-haystack]')?.dataset?.searchHaystack ??
        '';
      row.hidden = query !== '' && !haystack.includes(query);
    });

    this.#updateNoResults(query);
  }

  /**
   * Empty the search field and re-reveal every row. Opt-in: bind it to whatever
   * event ends the current search session — the CR annexes modal binds
   * `modal:close`, because that modal now closes after each successful add, so the
   * next opening is a NEW gesture and must start from the whole list rather than
   * from the query that produced the row you just consumed.
   *
   * Deliberately NOT wired to a Turbo stream replace: a row streamed in mid-search
   * must still obey the live query (see `#refilterIfSearching`). Clearing on
   * REPLACE is the bug this controller was written to prevent; clearing on CLOSE is
   * the opposite end of the same session.
   *
   * Guarded on an already-empty field so it can be bound to a frequent event
   * without re-running the O(N²) rescan for nothing.
   */
  clearSearch() {
    if (!this.hasSearchTarget) return;
    if (this.searchTarget.value === '') return;

    this.searchTarget.value = '';
    this.filter();
  }

  // Private

  // F11 — a row appended by a Turbo stream (W1) while a search is active must be
  // run through the filter too, else it shows despite not matching the query (and
  // #updateNoResults could then lie). Stateless — a bare guard on the framework
  // search target; at modal open the search is empty so this is a no-op (zero added
  // cost, no worsening of the accepted O(N²) full-list rescan).
  #refilterIfSearching() {
    if (this.hasSearchTarget && this.searchTarget.value.trim() !== '') this.filter();
  }

  #updateNoResults(query) {
    if (!this.hasNoResultsTarget || this.rowTargets.length === 0) return;

    const noMatches = query !== '' && this.rowTargets.every((row) => row.hidden);
    this.noResultsTarget.hidden = !noMatches;
  }

  #enabledCheckboxes() {
    return this.checkboxTargets.filter((cb) => !cb.disabled);
  }

  // Count of enabled (not disabled) AND checked checkboxes.
  #count() {
    return this.#enabledCheckboxes().filter((cb) => cb.checked).length;
  }

  // Count of DISABLED checkboxes (e.g. rows already on the CR) — feeds the
  // optional « · M déjà… » counter suffix (R10).
  #disabledCount() {
    return this.checkboxTargets.filter((cb) => cb.disabled).length;
  }

  #updateState() {
    this.#updateToggleLabel();
    this.#updateSubmitButton();
    this.#updateCounter();
  }

  #updateToggleLabel() {
    if (!this.hasToggleButtonTarget) return;

    const enabled = this.#enabledCheckboxes();
    const allChecked = enabled.length > 0 && enabled.every((cb) => cb.checked);

    this.toggleButtonTarget.textContent = allChecked
      ? this.deselectAllLabelValue
      : this.selectAllLabelValue;
  }

  #updateSubmitButton() {
    if (!this.hasSubmitButtonTarget) return;

    const count = this.#count();
    this.submitButtonTarget.disabled = count === 0;

    // Only rewrite the LABEL when a submit template is configured. Existing
    // callers leave `submitTemplate` empty (their submit button may hold an
    // icon child, which a textContent rewrite would clobber) → disable-only.
    if (this.submitTemplateValue) {
      this.submitButtonTarget.textContent =
        count === 0 ? this.submitZeroValue : this.submitTemplateValue.replace('{count}', count);
    }
  }

  #updateCounter() {
    if (!this.hasCounterTarget || !this.counterTemplateValue) return;

    const base = this.counterTemplateValue.replace('{count}', this.#count());
    const disabled = this.#disabledCount();
    // Suffix rendered ONLY when there is something to explain — otherwise a
    // pristine picker would read « 0 sélectionnée(s) · 0 déjà au CR ». Callers
    // that omit `counterDisabledTemplate` (clause/effectif pickers) get `base`
    // alone → byte-identical output.
    const suffix =
      disabled > 0 && this.counterDisabledTemplateValue
        ? this.counterDisabledTemplateValue.replace('{count}', disabled)
        : '';
    this.counterTarget.textContent = base + suffix;
  }

  // Refresh the optional live row-count badge (a tab pill) with the number of
  // `row` targets in THIS scope. Nested `checkbox-group` scopes own their own
  // rows, so this never counts the annuaire tab's rows. No-op without the target.
  #updateRowCount() {
    if (!this.hasRowCountTarget) return;

    this.rowCountTarget.textContent = this.rowTargets.length;
  }
}
